import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { autoValueFor } from '../utils/zodiacIndex';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl
} from '@mui/material';

const PLANETS = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];
const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
const NAKSHATRAS = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra', 'Punarvasu',
  'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni', 'Hasta',
  'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha', 'Mula', 'Purva Ashadha',
  'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha', 'Purva Bhadrapada',
  'Uttara Bhadrapada', 'Revati'
];
const DISTANCE_CATEGORIES = ['Close', 'Medium', 'Far', 'Very Far'];
const SPEED_CATEGORIES = ['Faster than average', 'Superfast', 'Average', 'Slower than average', 'Retrograde'];
const CHARANS = ['1', '2', '3', '4'];
const initForms = {
  r1:  { planet: 'Sun', harmonic: '2', offsetDeg: '2', withinHarmonic: '2',   value: '2' },
  r2:  { planet1: 'Sun', planet2: 'Moon', fromDecDiff: '2', toDeg: '2',       value: '2' },
  r3:  { planet: 'Sun', fromDecl: '2', toDecl: '2',                           value: '2' },
  r4:  { planet: 'Sun', distCat: 'Close',                                     value: '2' },
  r5:  { planet: 'Sun', speedCat: 'Faster than average',                      value: '2' },
  r6:  { planet1: 'Sun', planet2: 'Moon', nakshatra: 'Ashwini', charan: '1', value: '2' },
  r7:  { planet1: 'Sun', planet2: 'Moon', sign: 'Aries',                      value: '1' },
  r8:  { planet1: 'Sun', planet2: 'Moon', nakshatra: 'Ashwini',               value: '1' },
  r9:  { planet: 'Sun', fromDeg: '2', toDeg: '2',                             value: '2' },
  r10: { planet1: 'Sun', planet2: 'Moon', fromDeg: '2', toDeg: '2',          value: '2' },
  r12: { planet1: 'Sun', planet2: 'Moon', planet3: 'Mercury', tolerance: '5', value: '2' },
  r13: { planet1: 'Sun', planet2: 'Moon', sign: 'Aries',                      value: '1' },
  r14: { planet1: 'Sun', planet2: 'Moon', nakshatra: 'Ashwini',               value: '1' },
};

/* ── small shared helpers ── */
const PlanetSelect = ({ value, onChange, exclude }) => (
  <FormControl size="small" sx={{ minWidth: 110 }}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {PLANETS.filter(p => !exclude || !exclude.includes(p)).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
    </Select>
  </FormControl>
);
PlanetSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, exclude: PropTypes.array };

const SignSelect = ({ value, onChange }) => (
  <FormControl size="small" sx={{ minWidth: 120 }}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {SIGNS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
    </Select>
  </FormControl>
);
SignSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const NakshatraSelect = ({ value, onChange }) => (
  <FormControl size="small" sx={{ minWidth: 150 }}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {NAKSHATRAS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
    </Select>
  </FormControl>
);
NakshatraSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const CatSelect = ({ value, onChange, options, minWidth }) => (
  <FormControl size="small" sx={{ minWidth: minWidth || 140 }}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </Select>
  </FormControl>
);
CatSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, options: PropTypes.array.isRequired, minWidth: PropTypes.number };

const Lbl = ({ children }) => (
  <Typography sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{children}</Typography>
);
Lbl.propTypes = { children: PropTypes.node.isRequired };

const Num = ({ value, onChange, width }) => (
  <TextField size="small" type="number" value={value} onChange={e => onChange(e.target.value)} sx={{ width: width || 70 }} />
);
Num.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, width: PropTypes.number };

const LonField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value}
    onChange={e => {
      const v = parseFloat(e.target.value);
      if (e.target.value === '') { onChange(''); return; }
      if (!isNaN(v)) onChange(String(Math.min(359.9, Math.max(0, v))));
    }}
    inputProps={{ min: 0, max: 359.9, step: 0.1 }} sx={{ width: 70 }} />
);
LonField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const DeclField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value}
    onChange={e => {
      const v = parseFloat(e.target.value);
      if (e.target.value === '' || e.target.value === '-') { onChange(e.target.value); return; }
      if (!isNaN(v)) onChange(String(Math.min(40, Math.max(-40, v))));
    }}
    inputProps={{ min: -40, max: 40, step: 0.01 }} sx={{ width: 70 }} />
);
DeclField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const ValField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value} onChange={e => onChange(e.target.value)}
    inputProps={{ min: 1, step: 1 }} sx={{ width: 56 }} />
);
ValField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const AddBtn = ({ onClick, disabled }) => (
  <Button variant="contained" size="small" onClick={onClick} disabled={disabled}
    sx={{ minWidth: 56, px: 1.5, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}>
    ADD
  </Button>
);
AddBtn.propTypes = { onClick: PropTypes.func.isRequired, disabled: PropTypes.bool };

const Row = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
    border: '1px solid #d0d0d0', borderRadius: 1, p: 1, mb: 1, backgroundColor: '#fafafa' }}>
    {children}
  </Box>
);
Row.propTypes = { children: PropTypes.node.isRequired };

const Spacer = () => <Box sx={{ flex: 1 }} />;
const AddTail = ({ value, onChange, onClick, disabled }) => (
  <>
    <Lbl>Add value</Lbl>
    <ValField value={value} onChange={onChange} />
    <AddBtn onClick={onClick} disabled={disabled} />
  </>
);
AddTail.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, onClick: PropTypes.func.isRequired, disabled: PropTypes.bool };

/* ─────────────────────────── main component ─────────────────────────── */
const MOAGroup4Tab = ({ onAddCondition, forms: propForms, onFormsChange }) => {
  const [localForms, setLocalForms] = useState(initForms);
  const forms = propForms || localForms;
  const setForms = onFormsChange || setLocalForms;
  const set = (row, field, val) => setForms(f => ({ ...f, [row]: { ...f[row], [field]: val } }));

  /**
   * Sets a rashi/nakshatra field AND the row's "Add value" to that selection's ordinal.
   *
   * Both in one state update rather than two calls: two sequential setForms would each read the
   * same `f`, and the second would overwrite the first, leaving the value updated and the dropdown
   * unchanged. The field stays editable afterwards — this fills it in, it does not own it.
   */
  const setWithOrdinal = (row, field, val, kind) => setForms(f => ({
    ...f,
    [row]: { ...f[row], [field]: val, value: autoValueFor(kind, val, f[row].value) },
  }));
  const setPair = (row, f1, v, f2) => setForms(prev => {
    const updated = { ...prev[row], [f1]: v };
    if (updated[f2] === v) updated[f2] = PLANETS.find(p => p !== v) || PLANETS[0];
    return { ...prev, [row]: updated };
  });
  const setTriple = (row, f1, v, f2, f3) => setForms(prev => {
    const updated = { ...prev[row], [f1]: v };
    if (updated[f2] === v) updated[f2] = PLANETS.find(p => p !== v && p !== updated[f3]) || PLANETS[0];
    if (updated[f3] === v) updated[f3] = PLANETS.find(p => p !== v && p !== updated[f2]) || PLANETS[1];
    return { ...prev, [row]: updated };
  });

  const add = (type, condObj) => {
    const val = parseInt(condObj.value, 10);
    if (!val || val < 1) return;
    onAddCondition({ group: 4, type, ...condObj, value: val });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight="bold" fontSize={15} sx={{ textAlign: 'center', mb: 2, letterSpacing: 0.5 }}>
        Transit++ Criteria
      </Typography>

      {/* Row 1: Harmonic of Planet, offset degrees, within harmonic */}
      <Row>
        <Lbl>Harmonic</Lbl>
        <Num value={forms.r1.harmonic} onChange={v => set('r1', 'harmonic', v)} />
        <Lbl>of Planet</Lbl>
        <PlanetSelect value={forms.r1.planet} onChange={v => set('r1', 'planet', v)} />
        <Lbl>offset degrees</Lbl>
        <Num value={forms.r1.offsetDeg} onChange={v => set('r1', 'offsetDeg', v)} />
        <Lbl>And degrees within harmonic</Lbl>
        <Num value={forms.r1.withinHarmonic} onChange={v => set('r1', 'withinHarmonic', v)} />
        <Spacer />
        <AddTail value={forms.r1.value} onChange={v => set('r1', 'value', v)}
          onClick={() => add('g4_harmonic_planet', { planet: forms.r1.planet, harmonic: Number(forms.r1.harmonic), offsetDeg: Number(forms.r1.offsetDeg), withinHarmonic: Number(forms.r1.withinHarmonic), value: forms.r1.value })} />
      </Row>

      {/* Row 2: Planet And Planet From declination difference */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r2.planet1} onChange={v => setPair('r2', 'planet1', v, 'planet2')} exclude={[forms.r2.planet2]} />
        <Lbl>And Planet</Lbl>
        <PlanetSelect value={forms.r2.planet2} onChange={v => setPair('r2', 'planet2', v, 'planet1')} exclude={[forms.r2.planet1]} />
        <Lbl>From declination difference of</Lbl>
        <DeclField value={forms.r2.fromDecDiff} onChange={v => {
          set('r2', 'fromDecDiff', v);
          if (parseFloat(forms.r2.toDeg) <= parseFloat(v)) set('r2', 'toDeg', String(parseFloat(v) + 1));
        }} />
        <Lbl>degrees to</Lbl>
        <DeclField value={forms.r2.toDeg} onChange={v => {
          const from = parseFloat(forms.r2.fromDecDiff);
          const to = parseFloat(v);
          if (!isNaN(to) && to > from) set('r2', 'toDeg', v);
        }} />
        <Lbl>degrees</Lbl>
        <Spacer />
        <AddTail value={forms.r2.value} onChange={v => set('r2', 'value', v)}
          disabled={parseFloat(forms.r2.toDeg) <= parseFloat(forms.r2.fromDecDiff)}
          onClick={() => add('g4_decl_diff_range', { planet1: forms.r2.planet1, planet2: forms.r2.planet2, fromDecDiff: Number(forms.r2.fromDecDiff), toDeg: Number(forms.r2.toDeg), value: forms.r2.value })} />
      </Row>

      {/* Row 3: Planet From Declination To Declination */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r3.planet} onChange={v => set('r3', 'planet', v)} />
        <Lbl>From Declination</Lbl>
        <DeclField value={forms.r3.fromDecl} onChange={v => set('r3', 'fromDecl', v)} />
        <Lbl>To Declination</Lbl>
        <DeclField value={forms.r3.toDecl} onChange={v => set('r3', 'toDecl', v)} />
        <Spacer />
        <AddTail value={forms.r3.value} onChange={v => set('r3', 'value', v)}
          onClick={() => add('g4_decl_range', { planet: forms.r3.planet, fromDecl: Number(forms.r3.fromDecl), toDecl: Number(forms.r3.toDecl), value: forms.r3.value })} />
      </Row>

      {/* Row 4: Planet Distance category */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r4.planet} onChange={v => set('r4', 'planet', v)} />
        <Lbl>Distance category</Lbl>
        <CatSelect value={forms.r4.distCat} onChange={v => set('r4', 'distCat', v)} options={DISTANCE_CATEGORIES} />
        <Spacer />
        <AddTail value={forms.r4.value} onChange={v => set('r4', 'value', v)}
          onClick={() => add('g4_distance_category', { planet: forms.r4.planet, distCat: forms.r4.distCat, value: forms.r4.value })} />
      </Row>

      {/* Row 5: Planet Speed category */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r5.planet} onChange={v => set('r5', 'planet', v)} />
        <Lbl>Speed category</Lbl>
        <CatSelect value={forms.r5.speedCat} onChange={v => set('r5', 'speedCat', v)} options={SPEED_CATEGORIES} minWidth={180} />
        <Spacer />
        <AddTail value={forms.r5.value} onChange={v => set('r5', 'value', v)}
          onClick={() => add('g4_speed_category', { planet: forms.r5.planet, speedCat: forms.r5.speedCat, value: forms.r5.value })} />
      </Row>

      {/* Row 6: Planet Planet In nakshatra charan [charan dd] [within N] */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r6.planet1} onChange={v => setPair('r6', 'planet1', v, 'planet2')} exclude={[forms.r6.planet2]} />
        <Lbl>and Planet</Lbl>
        <PlanetSelect value={forms.r6.planet2} onChange={v => setPair('r6', 'planet2', v, 'planet1')} exclude={[forms.r6.planet1]} />
        <Lbl>in Nakshatra</Lbl>
        <NakshatraSelect value={forms.r6.nakshatra} onChange={v => set('r6', 'nakshatra', v)} />
        <Lbl>in charan</Lbl>
        <CatSelect value={forms.r6.charan} onChange={v => set('r6', 'charan', v)} options={CHARANS} minWidth={70} />
        <Spacer />
        <AddTail value={forms.r6.value} onChange={v => set('r6', 'value', v)}
          onClick={() => add('g4_nakshatra_charan', { planet1: forms.r6.planet1, planet2: forms.r6.planet2, nakshatra: forms.r6.nakshatra, charan: Number(forms.r6.charan), value: forms.r6.value })} />
      </Row>

      {/* Row 7: Planet Planet In rashi */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r7.planet1} onChange={v => setPair('r7', 'planet1', v, 'planet2')} exclude={[forms.r7.planet2]} />
        <Lbl>and Planet</Lbl>
        <PlanetSelect value={forms.r7.planet2} onChange={v => setPair('r7', 'planet2', v, 'planet1')} exclude={[forms.r7.planet1]} />
        <Lbl>In rashi</Lbl>
        <SignSelect value={forms.r7.sign} onChange={v => setWithOrdinal('r7', 'sign', v, 'rashi')} />
        <Spacer />
        <AddTail value={forms.r7.value} onChange={v => set('r7', 'value', v)}
          onClick={() => add('g4_in_rashi', { planet1: forms.r7.planet1, planet2: forms.r7.planet2, sign: forms.r7.sign, value: forms.r7.value })} />
      </Row>

      {/* Row 8: Planet Planet In nakshatra */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r8.planet1} onChange={v => setPair('r8', 'planet1', v, 'planet2')} exclude={[forms.r8.planet2]} />
        <Lbl>and Planet</Lbl>
        <PlanetSelect value={forms.r8.planet2} onChange={v => setPair('r8', 'planet2', v, 'planet1')} exclude={[forms.r8.planet1]} />
        <Lbl>In nakshatra</Lbl>
        <NakshatraSelect value={forms.r8.nakshatra} onChange={v => setWithOrdinal('r8', 'nakshatra', v, 'nakshatra')} />
        <Spacer />
        <AddTail value={forms.r8.value} onChange={v => set('r8', 'value', v)}
          onClick={() => add('g4_in_nakshatra', { planet1: forms.r8.planet1, planet2: forms.r8.planet2, nakshatra: forms.r8.nakshatra, value: forms.r8.value })} />
      </Row>

      {/* Row 9: Planet in longitude degree range from degrees to degrees */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r9.planet} onChange={v => set('r9', 'planet', v)} />
        <Lbl>in longitude degree range from degrees</Lbl>
        <LonField value={forms.r9.fromDeg} onChange={v => set('r9', 'fromDeg', v)} />
        <Lbl>to degrees</Lbl>
        <LonField value={forms.r9.toDeg} onChange={v => set('r9', 'toDeg', v)} />
        <Spacer />
        <AddTail value={forms.r9.value} onChange={v => set('r9', 'value', v)}
          onClick={() => add('g4_longitude_range', { planet: forms.r9.planet, fromDeg: Number(forms.r9.fromDeg), toDeg: Number(forms.r9.toDeg), value: forms.r9.value })} />
      </Row>

      {/* Row 10: Planet Planet at longitude difference from degrees to degrees */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r10.planet1} onChange={v => setPair('r10', 'planet1', v, 'planet2')} exclude={[forms.r10.planet2]} />
        <Lbl>and Planet</Lbl>
        <PlanetSelect value={forms.r10.planet2} onChange={v => setPair('r10', 'planet2', v, 'planet1')} exclude={[forms.r10.planet1]} />
        <Lbl>at longitude difference from degrees</Lbl>
        <LonField value={forms.r10.fromDeg} onChange={v => set('r10', 'fromDeg', v)} />
        <Lbl>to degrees</Lbl>
        <LonField value={forms.r10.toDeg} onChange={v => set('r10', 'toDeg', v)} />
        <Spacer />
        <AddTail value={forms.r10.value} onChange={v => set('r10', 'value', v)}
          onClick={() => add('g4_longi_diff_range', { planet1: forms.r10.planet1, planet2: forms.r10.planet2, fromDeg: Number(forms.r10.fromDeg), toDeg: Number(forms.r10.toDeg), value: forms.r10.value })} />
      </Row>

      {/* Row 12: Planet Midpoint between Planet And Planet Tolerance degrees */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r12.planet1} onChange={v => setTriple('r12', 'planet1', v, 'planet2', 'planet3')} exclude={[forms.r12.planet2, forms.r12.planet3]} />
        <Lbl>Midpoint between Planet</Lbl>
        <PlanetSelect value={forms.r12.planet2} onChange={v => setTriple('r12', 'planet2', v, 'planet1', 'planet3')} exclude={[forms.r12.planet1, forms.r12.planet3]} />
        <Lbl>And Planet</Lbl>
        <PlanetSelect value={forms.r12.planet3} onChange={v => setTriple('r12', 'planet3', v, 'planet1', 'planet2')} exclude={[forms.r12.planet1, forms.r12.planet2]} />
        <Lbl>Tolerance degrees</Lbl>
        <Num value={forms.r12.tolerance} onChange={v => {
          const n = parseFloat(v);
          set('r12', 'tolerance', isNaN(n) ? v : String(Math.min(5, Math.max(0, n))));
        }} />
        <Spacer />
        <AddTail value={forms.r12.value} onChange={v => set('r12', 'value', v)}
          onClick={() => add('g4_midpoint', { planet1: forms.r12.planet1, planet2: forms.r12.planet2, planet3: forms.r12.planet3, tolerance: Number(forms.r12.tolerance), value: forms.r12.value })} />
      </Row>

      {/* Row 13: Planet Conjunct Planet In rashi */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r13.planet1} onChange={v => setPair('r13', 'planet1', v, 'planet2')} exclude={[forms.r13.planet2]} />
        <Typography sx={{ fontSize: '1rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Conjunct</Typography>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r13.planet2} onChange={v => setPair('r13', 'planet2', v, 'planet1')} exclude={[forms.r13.planet1]} />
        <Lbl>In rashi</Lbl>
        <SignSelect value={forms.r13.sign} onChange={v => setWithOrdinal('r13', 'sign', v, 'rashi')} />
        <Spacer />
        <AddTail value={forms.r13.value} onChange={v => set('r13', 'value', v)}
          onClick={() => add('g4_conjunct_in_rashi', { planet1: forms.r13.planet1, planet2: forms.r13.planet2, sign: forms.r13.sign, value: forms.r13.value })} />
      </Row>

      {/* Row 14: Planet Conjunct Planet In Nakshatra */}
      <Row>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r14.planet1} onChange={v => setPair('r14', 'planet1', v, 'planet2')} exclude={[forms.r14.planet2]} />
        <Typography sx={{ fontSize: '1rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Conjunct</Typography>
        <Lbl>Planet</Lbl>
        <PlanetSelect value={forms.r14.planet2} onChange={v => setPair('r14', 'planet2', v, 'planet1')} exclude={[forms.r14.planet1]} />
        <Lbl>In Nakshatra</Lbl>
        <NakshatraSelect value={forms.r14.nakshatra} onChange={v => setWithOrdinal('r14', 'nakshatra', v, 'nakshatra')} />
        <Spacer />
        <AddTail value={forms.r14.value} onChange={v => set('r14', 'value', v)}
          onClick={() => add('g4_conjunct_in_nakshatra', { planet1: forms.r14.planet1, planet2: forms.r14.planet2, nakshatra: forms.r14.nakshatra, value: forms.r14.value })} />
      </Row>

    </Box>
  );
};

MOAGroup4Tab.propTypes = {
  onAddCondition: PropTypes.func.isRequired,
  forms: PropTypes.object,
  onFormsChange: PropTypes.func,
};

export default MOAGroup4Tab;
